import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { DollarSign, ExternalLink } from 'lucide-react';
import { getDealSalesAmount } from '@/utils/salesAmount';

const STAGE_CONFIG = {
  new: { label: 'Novo', color: 'hsl(var(--chart-1))' },
  contact: { label: 'Contato', color: 'hsl(var(--chart-2))' },
  scheduled: { label: 'Reunião Agendada', color: 'hsl(var(--chart-3))' },
  proposal: { label: 'Proposta', color: 'hsl(var(--chart-4))' },
  negotiation: { label: 'Negociação', color: 'hsl(var(--chart-5))' },
};

const getDaysColor = (days) => {
  if (days >= 90) return 'bg-destructive text-destructive-foreground';
  if (days >= 60) return 'bg-orange-500 text-white';
  if (days >= 30) return 'bg-amber-500 text-white';
  if (days >= 14) return 'bg-yellow-500 text-black';
  return 'bg-muted text-muted-foreground';
};

export default function DealAgingList({ deals, loading }) {
  const handleDealClick = (dealId) => {
    window.open(`/DealDetail?id=${dealId}`, '_blank');
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getUserName = (user) => {
    if (!user) return null;
    return user.display_name || 
      `${user.first_name || ''} ${user.last_name || ''}`.trim();
  };

  if (loading) {
    return (
      <div className="h-[400px] flex items-center justify-center text-muted-foreground">
        Carregando...
      </div>
    );
  }

  if (!deals.length) {
    return (
      <div className="h-[400px] flex items-center justify-center text-muted-foreground">
        No active deals to display
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px] pr-2">
      <div className="space-y-2">
          {deals.map((deal) => {
            const stage = (deal.stage ?? '').toLowerCase();
            const stageConfig = STAGE_CONFIG[stage] || { label: stage, color: 'hsl(var(--muted))' };
          
          return (
            <div
              key={deal.id}
              onClick={() => handleDealClick(deal.id)}
              className="p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors group"
            >
              <div className="flex items-center gap-3">
                {/* Days Badge */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center ${getDaysColor(deal.days_since_contact)}`}>
                  <span className="text-lg font-bold leading-none">{deal.days_since_contact}</span>
                  <span className="text-[10px] leading-none">days</span>
                </div>

                {/* Deal Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate text-sm">
                      {deal.contact?.first_name} {deal.contact?.last_name}
                    </span>
                    <Badge 
                      variant="outline" 
                      className="text-xs flex-shrink-0"
                      style={{ 
                        borderColor: stageConfig.color,
                        color: stageConfig.color,
                      }}
                    >
                      {stageConfig.label}
                    </Badge>
                    <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>Consultant: {getUserName(deal.sales_consultant_user) || '-'}</span>
                    <span>• SDR: {getUserName(deal.sales_development_user) || '-'}</span>
                  </div>
                </div>

                {/* Amount - valor efetivo */}
                <div className="flex items-center gap-1 text-sm font-semibold text-primary flex-shrink-0">
                  <DollarSign className="h-4 w-4" />
                  {formatCurrency(getDealSalesAmount({
                    deal_status: deal.deal_status,
                    closed_date: deal.closed_date,
                    cancelled_at: deal.cancelled_at,
                    total_amount: deal.total_amount,
                    amount_received: deal.amount_received || 0,
                    upsell_credit_amount: deal.upsell_credit_amount || 0,
                  }))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
